<template>
  <div class="sequence-tester">
    <div class="header">
      <h1>Word Wheel Letter Sequence Tester</h1>
      <div class="controls">
        <div class="button-group">
          <button @click="generateSequences(50)" class="btn btn-primary">
            Generate 50
          </button>
          <button @click="generateSequences(100)" class="btn btn-primary">
            Generate 100
          </button>
          <button @click="generateSequences(500)" class="btn btn-primary">
            Generate 500
          </button>
          <button @click="clearSequences" class="btn btn-danger">
            Clear All
          </button>
        </div>
        <div class="info">
          <p>
            Total sequences generated: <strong>{{ sequences.length }}</strong>
          </p>
        </div>
      </div>
    </div>

    <div class="sequences-container">
      <div
        v-for="(sequence, index) in sequences"
        :key="index"
        class="sequence-row"
      >
        <span class="sequence-number">{{ index + 1 }}</span>
        <div class="sequence-grid">
          <span
            v-for="(letter, letterIdx) in sequence"
            :key="letterIdx"
            class="letter"
            :class="{
              'letter-highlighted': isLetterHighlighted(sequence, letterIdx),
              'letter-match': getMatchedWord(sequence, letterIdx),
            }"
            :title="getMatchedWord(sequence, letterIdx) || ''"
          >
            {{ letter }}
          </span>
        </div>
        <div v-if="getWordsInSequence(sequence).length > 0" class="words-found">
          <span
            v-for="wordItem in getWordsInSequence(sequence)"
            :key="wordItem.word"
            class="word-badge"
          >
            {{ wordItem.word }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { ref, onMounted } from 'vue';
import wordListData from '@/components/GamePlay/Games/WordWheel/data/wordList.json';
import { SafeStringGenerator } from '@/shared/SafeStringGenerator';
import { useWordWheelStore } from '@/stores/wordWheelStore';

export default {
  name: 'WordWheelSequenceTester',
  setup() {
    const sequences = ref<string[][]>([]);
    const allWords = ref<Set<string>>(new Set());
    const safeStringGen = ref<SafeStringGenerator | null>(null);
    const wheelStore = useWordWheelStore();
    const letterPoolInitialized = ref(false);

    onMounted(async () => {
      // Load all words from word list
      const wordList = wordListData as Record<string, string[]>;
      Object.values(wordList).forEach((words) => {
        words.forEach((word) => {
          allWords.value.add(word.toUpperCase());
        });
      });

      // Initialize SafeStringGenerator with max word length from dictionary
      const maxWordLength = Object.keys(
        wordListData as Record<string, string[]>
      )
        .map((key) => parseInt(key))
        .reduce((max, len) => Math.max(max, len), 0);

      safeStringGen.value = new SafeStringGenerator(maxWordLength);

      // Initialize the letter pool in the store - SAME AS GAME
      console.log(
        '🔧 DEBUG: Initializing letter pool with SafeStringGenerator'
      );
      const letterPool = safeStringGen.value.generateLetterPool(500, 2, 7);
      wheelStore.initializeLetterPool(letterPool);
      letterPoolInitialized.value = true;
      console.log(
        '🔧 DEBUG: Letter pool initialized with',
        letterPool.length,
        'letters'
      );
    });

    const generateSingleSequence = (): string[] => {
      if (!letterPoolInitialized.value) {
        console.warn('🔧 DEBUG: Letter pool not initialized yet');
        return Array.from({ length: 12 }, () =>
          Math.random() < 0.3
            ? ['A', 'O', 'I', 'U', 'E', 'Y'][Math.floor(Math.random() * 6)]
            : ['M', 'N', 'S', 'G', 'D', 'H', 'F', 'R', 'T', 'B', 'W'][
                Math.floor(Math.random() * 11)
              ]
        );
      }

      // Check if letterPool has entries (it's a ref to an array)
      const poolArray =
        wheelStore.letterPool && Array.isArray(wheelStore.letterPool)
          ? wheelStore.letterPool
          : [];

      if (poolArray.length === 0) {
        console.warn('🔧 DEBUG: Letter pool is empty or invalid');
        return Array.from({ length: 12 }, () => 'X');
      }

      // Use the EXACT SAME WORKFLOW as the game:
      // 1. Draw 12 letters from the store's safe pool
      const letterArr = wheelStore.drawFromPool(12);
      console.log('🔧 DEBUG: Drew from pool:', letterArr.join(''));

      // 2. CRITICAL: Sanitize the drawn letters to remove any words that formed
      // across chunk boundaries in the pool
      const sanitizedArr = safeStringGen.value!.sanitizeLetterArray(
        letterArr,
        2,
        7
      );
      console.log('🔧 DEBUG: After sanitization:', sanitizedArr.join(''));

      // 3. Set the sanitized letter array in the store
      wheelStore.setLetterArray(sanitizedArr);

      // Return the result to display
      console.log('🔧 DEBUG: Final array for display:', sanitizedArr.join(''));
      return sanitizedArr;
    };

    const generateSequences = (count: number) => {
      for (let i = 0; i < count; i++) {
        sequences.value.push(generateSingleSequence());
      }
    };

    const clearSequences = () => {
      sequences.value = [];
    };

    const getWordsInSequence = (
      sequence: string[]
    ): Array<{ word: string; startIdx: number }> => {
      const foundWords: Array<{ word: string; startIdx: number }> = [];

      // Check only for 2-letter consecutive words
      for (let start = 0; start < sequence.length - 1; start++) {
        const twoLetterWord = sequence.slice(start, start + 2).join('');
        if (allWords.value.has(twoLetterWord)) {
          foundWords.push({ word: twoLetterWord, startIdx: start });
        }
      }

      // Remove duplicates and sort by word
      const uniqueWords = Array.from(
        new Map(foundWords.map((item) => [item.word, item])).values()
      );
      return uniqueWords.sort((a, b) => a.word.localeCompare(b.word));
    };

    const isLetterPartOfTwoLetterWord = (
      sequence: string[],
      letterIdx: number
    ): boolean => {
      // Check if this letter is the first letter of a 2-letter word
      if (letterIdx < sequence.length - 1) {
        const twoLetterWord = sequence.slice(letterIdx, letterIdx + 2).join('');
        if (allWords.value.has(twoLetterWord)) {
          return true;
        }
      }

      // Check if this letter is the second letter of a 2-letter word
      if (letterIdx > 0) {
        const twoLetterWord = sequence
          .slice(letterIdx - 1, letterIdx + 1)
          .join('');
        if (allWords.value.has(twoLetterWord)) {
          return true;
        }
      }

      return false;
    };

    const getMatchedWord = (sequence: string[], letterIdx: number): string => {
      // Check if this letter starts a 2-letter word
      if (letterIdx < sequence.length - 1) {
        const twoLetterWord = sequence.slice(letterIdx, letterIdx + 2).join('');
        if (allWords.value.has(twoLetterWord)) {
          return twoLetterWord;
        }
      }

      // Check if this letter is the second letter of a 2-letter word
      if (letterIdx > 0) {
        const twoLetterWord = sequence
          .slice(letterIdx - 1, letterIdx + 1)
          .join('');
        if (allWords.value.has(twoLetterWord)) {
          return twoLetterWord;
        }
      }

      return '';
    };

    const isLetterHighlighted = (
      sequence: string[],
      letterIdx: number
    ): boolean => {
      return isLetterPartOfTwoLetterWord(sequence, letterIdx);
    };

    return {
      sequences,
      generateSequences,
      clearSequences,
      getWordsInSequence,
      getMatchedWord,
      isLetterHighlighted,
    };
  },
};
</script>

<style scoped>
.sequence-tester {
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
}

.header {
  margin-bottom: 30px;
}

.header h1 {
  margin: 0 0 20px 0;
  color: #333;
  font-size: 28px;
}

.controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 20px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}

.button-group {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.btn {
  padding: 10px 20px;
  font-size: 14px;
  font-weight: 600;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-primary {
  background-color: #007bff;
  color: white;
}

.btn-primary:hover {
  background-color: #0056b3;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 123, 255, 0.3);
}

.btn-danger {
  background-color: #dc3545;
  color: white;
}

.btn-danger:hover {
  background-color: #c82333;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(220, 53, 69, 0.3);
}

.info {
  padding: 10px 15px;
  background-color: #f0f0f0;
  border-radius: 6px;
  font-size: 16px;
  color: #333;
}

.sequences-container {
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-height: 70vh;
  overflow-y: auto;
  padding: 10px;
  background-color: #f9f9f9;
  border-radius: 8px;
}

.sequence-row {
  display: flex;
  align-items: center;
  gap: 15px;
  padding: 12px;
  background-color: white;
  border-radius: 6px;
  border: 1px solid #e0e0e0;
  transition: all 0.2s ease;
}

.sequence-row:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border-color: #007bff;
}

.sequence-number {
  display: inline-block;
  min-width: 50px;
  font-weight: 600;
  color: #666;
  font-size: 14px;
  text-align: right;
}

.sequence-grid {
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  gap: 6px;
  flex: 1;
}

.letter {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background-color: white;
  border: 2px solid #333;
  border-radius: 4px;
  font-weight: 700;
  font-size: 14px;
  color: #000;
  user-select: none;
  transition: all 0.2s ease;
}

.letter:hover {
  background-color: #f0f0f0;
  transform: scale(1.05);
}

.letter-highlighted {
  background-color: #ffd700;
  font-weight: 900;
  border-color: #ff9500;
  box-shadow: 0 0 8px rgba(255, 165, 0, 0.5);
}

.letter-match {
  color: #000;
}

.words-found {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  margin-left: 10px;
  align-items: center;
}

.word-badge {
  display: inline-block;
  background-color: #4caf50;
  color: white;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
}

/* Scrollbar styling */
.sequences-container::-webkit-scrollbar {
  width: 8px;
}

.sequences-container::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.sequences-container::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 4px;
}

.sequences-container::-webkit-scrollbar-thumb:hover {
  background: #555;
}

@media (max-width: 768px) {
  .sequence-tester {
    padding: 15px;
  }

  .controls {
    flex-direction: column;
    align-items: stretch;
  }

  .button-group {
    width: 100%;
  }

  .btn {
    flex: 1;
  }

  .sequence-row {
    flex-direction: column;
  }

  .sequence-grid {
    gap: 4px;
  }

  .letter {
    width: 28px;
    height: 28px;
    font-size: 12px;
    border-width: 1px;
  }

  .words-found {
    margin-left: 0;
    margin-top: 8px;
  }

  .word-badge {
    font-size: 11px;
  }
}
</style>
